import React from 'react';
import {
  Box,
  Stack,
  TextField,
  InputAdornment,
  IconButton,
  Autocomplete,
  Checkbox,
  FormControlLabel,
  Badge,
  Button,
  Typography,
  Paper,
  Collapse,
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import ClearIcon from '@mui/icons-material/Clear';
import FilterListIcon from '@mui/icons-material/FilterList';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import CalendarMonthRoundedIcon from '@mui/icons-material/CalendarMonthRounded';
import dayjs from 'dayjs';

import { sortProjectRows } from '../../utils/functions/sortProjects';
import ExpensesDateRangePicker from './ExpensesDateRangePicker';

// แถวเดียวของโหมด "เพิ่มเติม" — ชื่อคอลัมน์ (label) ชิดซ้าย + ตัวควบคุมจริงชิดขวา เรียงเป็นตารางเดียวกันทุกแถว
// (แนว Compass query bar) วางเป็น 2 คอลัมน์บนจอ sm+ (borderRight คั่นคอลัมน์ซ้าย/ขวา, borderBottom คั่นแต่ละแถว)
function FilterRow({ label, children, borderRight, borderBottom }) {
  return (
    <Box
      sx={{
        display: 'grid',
        gridTemplateColumns: { xs: '1fr', sm: '140px 1fr' },
        gap: { xs: 0.5, sm: 1.5 },
        alignItems: 'center',
        px: 1.75,
        py: 1.1,
        borderBottom: borderBottom ? '1px solid' : 'none',
        borderRight: { xs: 'none', sm: borderRight ? '1px solid' : 'none' },
        borderColor: 'divider',
        transition: 'background-color 0.15s ease',
        '&:hover': {
          bgcolor: (theme) => (theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.025)' : 'rgba(15,23,42,0.02)'),
        },
      }}
    >
      <Typography variant="body2" fontWeight={700} color="text.primary">
        {label}
      </Typography>
      <Box sx={{ minWidth: 0 }}>{children}</Box>
    </Box>
  );
}

// กล่อง "ตัวกรอง" ทั้งกล่อง (header collapse + ช่วงวันที่ + search + ปุ่ม "เพิ่มเติม" + panel เพิ่มเติม +
// สรุปช่วงวันที่ที่ apply อยู่) — เดิม copy วางซ้ำเป๊ะๆ อยู่ใน 6 ไฟล์ tab (ExpensesTabBacklog/Future/Archive/
// All/Rejected/Labor.js) ดึงมารวมเป็น component เดียว แก้ layout/behavior ทีเดียวมีผลทุก tab
// เป็น controlled component ล้วนๆ — ไม่ถือ state เอง รับค่า+setter มาจาก parent ทั้งหมด (parent ยังคุม
// fetchPage/handleSearch ของตัวเองอยู่ เพราะ handler ที่ส่งต่อให้ ExpensesTableApplove ต่างกันไปตาม tab)
export default function ExpensesFilterPanel({
  filterOpen,
  onToggleFilterOpen,
  maxLookbackText,
  showDateRange,
  dateRangeRef,
  dateRange,
  dateBounds,
  dateFilterField,
  onDateFieldChange,
  onMaxLookbackChange,
  searchInput,
  onSearchInputChange,
  onSearch,
  onClearSearch,
  searchPlaceholder = 'ค้นหา: รายการ / รหัส EX / ผู้รับเงิน / ผู้ขอเบิก / เลขที่โครงการ / เลขที่งบประมาณ / ยอดจ่ายจริง',
  advancedOpen,
  onToggleAdvanced,
  projects,
  projectSelect,
  onProjectChange,
  budgets,
  budgetSelect,
  onBudgetChange,
  filterVat,
  onFilterVatChange,
  filterWithholding,
  onFilterWithholdingChange,
  advSearchName,
  onAdvSearchNameChange,
  advSearchCode,
  onAdvSearchCodeChange,
  advSearchPayee,
  onAdvSearchPayeeChange,
  advSearchApplicant,
  onAdvSearchApplicantChange,
  advSearchProjectNumber,
  onAdvSearchProjectNumberChange,
  advSearchBudgetNumber,
  onAdvSearchBudgetNumberChange,
  advSearchNetAmount,
  onAdvSearchNetAmountChange,
  onClearAdvanced,
  appliedDateFilterField,
  appliedDateRange,
}) {
  const activeTaxFilterCount = (filterVat ? 1 : 0) + (filterWithholding ? 1 : 0);
  const activeAdvancedCount =
    [advSearchName, advSearchCode, advSearchPayee, advSearchApplicant, advSearchProjectNumber, advSearchBudgetNumber, advSearchNetAmount]
      .filter(Boolean).length
    + (projectSelect ? 1 : 0)
    + (budgetSelect ? 1 : 0)
    + activeTaxFilterCount;

  const advTextField = (value, onChange, placeholder) => (
    <TextField
      size="small"
      fullWidth
      placeholder={placeholder}
      value={value}
      onChange={(e) => onChange(e.target.value)}
      onKeyDown={(e) => { if (e.key === 'Enter') onSearch(); }}
      sx={{ maxWidth: 420, '& .MuiOutlinedInput-root': { borderRadius: '10px' } }}
    />
  );

  const advancedRows = [
    {
      label: 'โครงการ',
      content: (
        <Autocomplete
          size="small"
          fullWidth
          options={sortProjectRows(projects?.rows || [])}
          value={projectSelect}
          onChange={(_, val) => onProjectChange(val)}
          getOptionLabel={(o) => `${o?.project_number || ''} ${o?.name || ''}`.trim()}
          isOptionEqualToValue={(o, v) => o._id === v._id}
          sx={{ maxWidth: 420, '& .MuiOutlinedInput-root': { borderRadius: '10px' } }}
          renderInput={(params) => <TextField {...params} placeholder="เลือกจากลิสต์" size="small" />}
        />
      ),
    },
    {
      label: 'งบประมาณ',
      content: (
        <Autocomplete
          size="small"
          fullWidth
          options={budgets?.rows || []}
          value={budgetSelect}
          onChange={(_, val) => onBudgetChange(val)}
          disabled={!projectSelect}
          getOptionLabel={(o) => `${o?.prefix || ''}${o?.budget_number || ''} ${o?.name || ''}`.trim()}
          isOptionEqualToValue={(o, v) => o._id === v._id}
          sx={{ maxWidth: 420, '& .MuiOutlinedInput-root': { borderRadius: '10px' } }}
          renderInput={(params) => (
            <TextField {...params} placeholder={projectSelect ? 'เลือกจากลิสต์' : 'เลือกโครงการก่อน'} size="small" />
          )}
        />
      ),
    },
    {
      label: 'ภาษี',
      content: (
        <Stack direction="row" spacing={2.5}>
          <FormControlLabel
            control={<Checkbox checked={filterVat} onChange={() => onFilterVatChange(!filterVat)} size="small" />}
            label={<Typography variant="body2">VAT 7%</Typography>}
          />
          <FormControlLabel
            control={<Checkbox checked={filterWithholding} onChange={() => onFilterWithholdingChange(!filterWithholding)} size="small" />}
            label={<Typography variant="body2">หัก ณ ที่จ่าย</Typography>}
          />
        </Stack>
      ),
    },
    { label: 'รายการ', content: advTextField(advSearchName, onAdvSearchNameChange, 'ค้นหารายการ') },
    { label: 'รหัส EX', content: advTextField(advSearchCode, onAdvSearchCodeChange, 'ค้นหารหัส EX') },
    { label: 'ผู้รับเงิน', content: advTextField(advSearchPayee, onAdvSearchPayeeChange, 'ค้นหาผู้รับเงิน') },
    { label: 'ผู้ขอเบิก', content: advTextField(advSearchApplicant, onAdvSearchApplicantChange, 'ค้นหาผู้ขอเบิก') },
    { label: 'เลขที่โครงการ', content: advTextField(advSearchProjectNumber, onAdvSearchProjectNumberChange, 'ค้นหาเลขที่โครงการ') },
    { label: 'เลขที่งบประมาณ', content: advTextField(advSearchBudgetNumber, onAdvSearchBudgetNumberChange, 'ค้นหาเลขที่งบประมาณ') },
    { label: 'ยอดจ่ายจริง', content: advTextField(advSearchNetAmount, onAdvSearchNetAmountChange, 'ค้นหายอดจ่ายจริง') },
  ];
  const ADV_COLS = 2;
  const advTotalRows = Math.ceil(advancedRows.length / ADV_COLS);

  return (
    <Paper
      elevation={0}
      sx={{
        p: 1.5,
        mb: 2,
        borderRadius: '16px',
        border: '1px solid',
        borderColor: 'divider',
        bgcolor: 'background.paper',
        boxShadow: (theme) => (theme.palette.mode === 'dark'
          ? '0 4px 16px rgba(0,0,0,0.3)'
          : '0 4px 16px rgba(15,23,42,0.04)'),
      }}
    >
      <Box
        onClick={onToggleFilterOpen}
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          cursor: 'pointer',
          userSelect: 'none',
          pb: filterOpen ? 1 : 0,
          gap: 1,
        }}
      >
        <Stack direction="row" spacing={1.5} alignItems="center" flexWrap="wrap" useFlexGap sx={{ minWidth: 0 }}>
          <Stack direction="row" spacing={0.75} alignItems="center" sx={{ flexShrink: 0 }}>
            <FilterListIcon sx={{ fontSize: 18, color: 'primary.main' }} />
            <Typography variant="body2" fontWeight={700} color="text.primary">
              ตัวกรอง
            </Typography>
          </Stack>
          {maxLookbackText && (
            <Typography variant="caption" color="text.secondary" onClick={(e) => e.stopPropagation()}>
              {maxLookbackText}
            </Typography>
          )}
        </Stack>
        <IconButton size="small" sx={{ transition: 'transform 0.2s ease', transform: filterOpen ? 'rotate(180deg)' : 'none' }}>
          <KeyboardArrowDownIcon fontSize="small" />
        </IconButton>
      </Box>
      <Collapse in={filterOpen}>
        <Stack direction={{ xs: 'column', lg: 'row' }} spacing={1} alignItems={{ xs: 'stretch', lg: 'center' }} flexWrap="wrap" useFlexGap>
          {showDateRange && (
            <Box sx={{ width: '100%' }}>
              <ExpensesDateRangePicker
                ref={dateRangeRef}
                value={dateRange}
                minDate={dateBounds.min}
                maxDate={dateBounds.max}
                onMaxLookbackChange={onMaxLookbackChange}
                dateField={dateFilterField}
                onDateFieldChange={onDateFieldChange}
              />
            </Box>
          )}
          <TextField
            size="small"
            placeholder={searchPlaceholder}
            value={searchInput}
            onChange={(e) => onSearchInputChange(e.target.value)}
            onKeyDown={(e) => { if (e.key === 'Enter') onSearch(); }}
            sx={{
              minWidth: 200,
              flex: 1,
              '& .MuiOutlinedInput-root': { borderRadius: '10px', bgcolor: (theme) => (theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.04)' : 'rgba(15,23,42,0.02)') },
            }}
            InputProps={{
              startAdornment: <InputAdornment position="start"><SearchIcon fontSize="small" sx={{ color: 'text.disabled' }} /></InputAdornment>,
              endAdornment: searchInput && (
                <InputAdornment position="end">
                  <IconButton size="small" onClick={onClearSearch}>
                    <ClearIcon fontSize="small" />
                  </IconButton>
                </InputAdornment>
              ),
            }}
          />
          <Button
            variant="contained"
            disableElevation
            startIcon={<SearchIcon />}
            onClick={onSearch}
            sx={{
              borderRadius: '10px',
              px: 2.5,
              flexShrink: 0,
              fontWeight: 700,
              background: 'linear-gradient(135deg, #6366f1 0%, #8b5cf6 100%)',
              boxShadow: '0 4px 12px -3px rgba(99,102,241,0.5)',
              transition: 'transform 0.2s ease, box-shadow 0.2s ease',
              '&:hover': {
                background: 'linear-gradient(135deg, #6366f1 0%, #8b5cf6 100%)',
                transform: 'translateY(-1px)',
                boxShadow: '0 6px 16px -3px rgba(99,102,241,0.6)',
              },
            }}
          >
            ค้นหา
          </Button>
          <Badge badgeContent={activeAdvancedCount} color="primary" invisible={activeAdvancedCount === 0}>
            <Button
              size="small"
              variant={advancedOpen ? 'contained' : 'outlined'}
              startIcon={<FilterListIcon />}
              endIcon={<KeyboardArrowDownIcon sx={{ transition: 'transform 0.2s ease', transform: advancedOpen ? 'rotate(180deg)' : 'none' }} />}
              onClick={onToggleAdvanced}
              sx={{ whiteSpace: 'nowrap', borderRadius: '10px', flexShrink: 0 }}
            >
              เพิ่มเติม
            </Button>
          </Badge>
        </Stack>

        <Collapse in={advancedOpen}>
          <Box sx={{ mt: 1.5, pt: 1.5, borderTop: '1px dashed', borderColor: 'divider' }}>
            <Stack direction={{ xs: 'column', sm: 'row' }} justifyContent="space-between" alignItems={{ xs: 'flex-start', sm: 'center' }} spacing={0.5} sx={{ mb: 1, px: 0.25 }}>
              <Typography variant="caption" color="text.secondary">
                กรอกได้พร้อมกันหลายช่อง — ผลลัพธ์ต้องตรงกับทุกช่องที่กรอก
              </Typography>
              {activeAdvancedCount > 0 && (
                <Button size="small" color="inherit" onClick={onClearAdvanced} sx={{ textTransform: 'none', color: 'text.secondary', minWidth: 0, p: 0 }}>
                  ล้างตัวกรองเพิ่มเติมทั้งหมด
                </Button>
              )}
            </Stack>

            <Box
              sx={{
                borderRadius: '14px',
                border: '1px solid',
                borderColor: 'divider',
                overflow: 'hidden',
                bgcolor: (theme) => (theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.015)' : 'rgba(15,23,42,0.012)'),
                display: 'grid',
                gridTemplateColumns: { xs: '1fr', sm: `repeat(${ADV_COLS}, 1fr)` },
              }}
            >
              {advancedRows.map((row, i) => (
                <FilterRow
                  key={row.label}
                  label={row.label}
                  borderRight={i % ADV_COLS !== ADV_COLS - 1 && i + 1 < advancedRows.length}
                  borderBottom={Math.floor(i / ADV_COLS) !== advTotalRows - 1}
                >
                  {row.content}
                </FilterRow>
              ))}
            </Box>
          </Box>
        </Collapse>
      </Collapse>

      {showDateRange && appliedDateRange?.[0] && appliedDateRange?.[1] && (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, mt: filterOpen ? 1.25 : 0, pt: filterOpen ? 1.25 : 0, borderTop: filterOpen ? '1px dashed' : 'none', borderColor: 'divider' }}>
          <CalendarMonthRoundedIcon sx={{ fontSize: 16, color: 'primary.main' }} />
          <Typography variant="body2" color="text.secondary">
            แสดงรายการ{appliedDateFilterField === 'createdAt' ? 'วันที่สร้างรายการ' : 'วันที่ขอรับเงิน'}ช่วง <b>{dayjs(appliedDateRange[0]).format('DD/MM/YYYY')}</b> ถึง <b>{dayjs(appliedDateRange[1]).format('DD/MM/YYYY')}</b>
          </Typography>
        </Box>
      )}
    </Paper>
  );
}
